import { Navigate, Outlet, useLocation } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { ShieldOff } from "lucide-react";
import { canAccess, canReadResource, hasPermission, type Permission } from "@hisense/shared";
import { landingFor, menuKeyForPath } from "@/app/nav";
import { useAuth } from "@/app/auth";
import { useCan } from "@/app/access";
import { PageBody } from "@/components/ui/PageHeader";
import { Button } from "@/components/ui/Button";

/**
 * Chặn cả một nhánh route khi vai trò không có quyền.
 *
 * Trước đây gõ thẳng `/users` hay `/settings` sẽ DỰNG XONG toàn bộ giao diện
 * quản trị, gọi API, rồi mới hiện lỗi 403 — người dùng thấy thoáng qua cả màn
 * hình mình không được vào, và mỗi lần như vậy là một loạt request vô ích.
 *
 * Đây là tiện ích hiển thị, KHÔNG phải hàng rào an ninh: mọi route API vẫn gác
 * độc lập. Chặn ở đây chỉ để không dựng thứ chắc chắn sẽ bị từ chối.
 */
export function RequirePermission({
  perm,
  resource,
}: {
  perm?: Permission;
  resource?: string;
}) {
  const { t } = useTranslation();
  const { user } = useAuth();
  const can = useCan();
  const { pathname } = useLocation();
  if (!user) return null; // RequireAuth ở ngoài đã lo phần chưa đăng nhập

  // Tài khoản có bảng quyền riêng: hỏi đúng bảng đó, không suy lại từ vai trò.
  const menuKey = user.access?.mode === "custom" ? menuKeyForPath(pathname) : undefined;
  const allowed = user.access?.mode === "custom"
    ? !!menuKey && canAccess(user.access, menuKey, "view")
    : perm
      ? can(perm)
      : resource
        ? canReadResource(user.role, resource)
        : can("records:read");

  if (allowed) return <Outlet />;

  return (
    <PageBody>
      <div className="mx-auto mt-16 max-w-md text-center">
        <ShieldOff className="mx-auto size-8 text-ink-faint" aria-hidden />
        <h1 className="mt-3 text-lg font-semibold text-ink">{t("errors.forbiddenTitle")}</h1>
        <p className="mt-1 text-sm text-ink-soft">{t("errors.forbiddenBody")}</p>
        <Button className="mt-4" size="sm" onClick={() => { window.location.href = landingFor(user.role, user.portalSupplierId, user.access); }}>
          {t("errors.forbiddenGoHome")}
        </Button>
      </div>
    </PageBody>
  );
}

/**
 * "/" là Dashboard, cần `dashboard:view` — mà kế toán KHÔNG có, nên họ đăng
 * nhập xong là rơi thẳng vào màn hình lỗi. Đưa họ tới trang đầu tiên thực sự
 * mở được.
 */
export function LandingRedirect({ children }: { children: React.ReactNode }) {
  const { user } = useAuth();
  if (user?.portalSupplierId) return <Navigate to={landingFor(user.role, user.portalSupplierId, user.access)} replace />;
  const noDashboard = user?.access?.mode === "custom"
    ? !canAccess(user.access, "dashboard", "view")
    : !!user && !hasPermission(user.role, "dashboard:view");
  if (user && noDashboard) {
    return <Navigate to={landingFor(user.role, user.portalSupplierId, user.access)} replace />;
  }
  return <>{children}</>;
}
